<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import {useQuasar} from 'quasar';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLoadingDataRefNull} from '@/ts/composables/pure/use-loading-data-ref';
import {useLoadingSpinner} from '@/ts/composables/pure/use-loading-notification';
import {useSignIn} from '@/ts/composables/stateful/use-auth';
import {usePasswordRequirements} from '@/ts/composables/pure/use-password-requirements';
import PublicAuthTemplate from '@/vue/templates/public-auth-template.vue';
import LoadingError from '@/vue/templates/loading-error.vue';
import SignUpLegalDocsModal from '@/vue/molecules/sign-up-legal-docs-modal.vue';
import PasswordRequirementsChecklist from '@/vue/molecules/password-requirements-checklist.vue';
import BalmPasswordInput from '@/vue/atoms/balm-password-input.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import SignUpCaptcha from '@/vue/organisms/sign-up/sign-up-captcha.vue';
import {LegalModel} from '@/ts/types/component/public-sign-up-page';
import type {NavigationItem} from '@/ts/types/component/navigation';
import type {SignUpCaptchaApi} from '@/ts/types/component/sign-up-captcha';
import {AppError} from '@/ts/utils/errors';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';
import {PRIVATE_CAPTCHA_RESEND_SITE_KEY, PRIVATE_CAPTCHA_SITE_KEY} from '@/ts/utils/consts';
import {persistentStorage} from '@/ts/instances/persistant-storage';

const api = useApi();
const route = useRoute();
const {t} = useI18n();
const $q = useQuasar();
const signIn = useSignIn();

const [legalDocs, loading, error] = useLoadingDataRefNull(async() => api.public.getLegalDocs());

// The emailed link is `.../public/verify-email?userID=...&code=...&orgID=...`.
// In PR previews (hash history) the query can precede the `#`, where
// `useRoute().query` can't see it — fall back to window.location.search.
function readQueryParam(name: string): string {
  const value = route.query?.[name];
  return (Array.isArray(value) ? value[0] : value)
    ?? new URLSearchParams(window.location.search).get(name)
    ?? '';
}

// The emailed link uses `userID` (Zitadel template naming); the BE body expects `userId`.
const userId = readQueryParam('userID');
const code = ref(readQueryParam('code'));
const password = ref('');
const confirmPassword = ref('');
const codeError = ref('');
const verified = ref(false);

const {requirements, allMet} = usePasswordRequirements(password, confirmPassword);
const canSubmit = computed((): boolean => Boolean(userId) && Boolean(code.value.trim()) && allMet.value);

// Verify and resend run under the dedicated Private Captcha property so they
// can be tracked separately from registration; fall back to the registration
// key (the BE validates every token against the same account) until every
// environment has the dedicated property.
const captchaSitekey = PRIVATE_CAPTCHA_RESEND_SITE_KEY ?? PRIVATE_CAPTCHA_SITE_KEY;
const isCaptchaActive = Boolean(captchaSitekey);
const captchaDialogOpened = ref(false);
const captchaRef = ref<SignUpCaptchaApi | null>(null);
// CDN load failure surfaced inside the dialog (same pattern as the wizard's
// captcha step); the component resets it on every remount attempt.
const captchaScriptFailed = ref(false);
// Both actions share the captcha dialog; this records which one the solved
// token belongs to.
const captchaAction = ref<'verify' | 'resend'>('resend');

const [doVerify] = useLoadingSpinner(
  async(captchaToken: string | null) => {
    codeError.value = '';
    try {
      await api.public.verifyEmail({
        userId,
        code: code.value.trim(),
        password: password.value,
        captchaToken,
      });
    } catch (err) {
      // Captcha tokens are single-use: close the dialog so a retry mounts a
      // fresh challenge; the error surfaces on the form underneath.
      captchaDialogOpened.value = false;
      if (err instanceof AppError && err.statusCode === ErrorCodesDto.INVALID_VERIFICATION_CODE) {
        codeError.value = t('invalid_verification_code');
        return;
      }
      throw err;
    }
  },
  () => {
    captchaDialogOpened.value = false;
    if (!codeError.value) {
      verified.value = true;
    }
  },
);

function requestCaptcha(action: 'verify' | 'resend'): void {
  captchaAction.value = action;
  captchaDialogOpened.value = true;
}

// Guard here rather than inside the spinner callback: an early return there
// would still run the success handler and flip the page to the verified view.
function onSubmit(): void {
  if (!canSubmit.value) {
    return;
  }
  if (isCaptchaActive) {
    requestCaptcha('verify');
  } else {
    void doVerify(null);
  }
}

const [doResend] = useLoadingSpinner(
  async(captchaToken: string | null) => {
    try {
      await api.public.resendVerificationEmail({userId, captchaToken});
    } catch (err) {
      // Private Captcha tokens are single-use: force a fresh challenge on retry.
      await captchaRef.value?.resetAndRemount();
      throw err;
    }
  },
  () => {
    captchaDialogOpened.value = false;
    $q.notify({
      type: 'positive',
      message: t('verification_code_sent'),
    });
  },
);

function onResendClick(): void {
  if (isCaptchaActive) {
    requestCaptcha('resend');
  } else {
    void doResend(null);
  }
}

// Submit as soon as the captcha is solved (the widget lives in a dialog, so
// there is no separate confirm button).
watch(() => captchaRef.value?.captchaToken, (token) => {
  if (token && captchaDialogOpened.value) {
    if (captchaAction.value === 'verify') {
      void doVerify(token);
    } else {
      void doResend(token);
    }
  }
});

const [goToLogin] = useLoadingSpinner(async() => signIn(persistentStorage.signUpEmail));

const legalModel = ref<LegalModel | null>(null);
const footerItems = ref<NavigationItem[]>([
  {id: LegalModel.IMPRINT, labelKey: 'imprint'},
  {id: LegalModel.TERMS_OF_SERVICE, labelKey: 'terms_of_use'},
  {id: LegalModel.PRIVACY_POLICY, labelKey: 'privacy_policy'},
]);

function onClickFooterItem(itemId: string): void {
  legalModel.value = itemId as LegalModel;
}
</script>
<template>
  <loading-error id="public-verify-email-page" :loading="loading" type="page" :error="error">
    <public-auth-template :footer-items="footerItems" @click-footer-item="onClickFooterItem">
      <sign-up-legal-docs-modal v-if="legalDocs" v-model:legal-model="legalModel" :legal-docs="legalDocs"/>
      <div v-if="verified" class="verify-email verify-email--success">
        <div class="success-badge" aria-hidden="true">
          <q-icon name="check" size="28px"/>
        </div>
        <h1>{{ $t('verification_complete') }}</h1>
        <p class="subtitle">
          {{ $t('verification_complete_text') }}
        </p>
        <balm-btn
          class="full-btn"
          :label="$t('log_in')"
          theme="blue"
          icon="arrow-narrow-right.svg"
          @click="goToLogin"
        />
      </div>
      <div v-else class="verify-email">
        <h1>{{ $t('verify_email_title') }}</h1>
        <p class="subtitle">
          {{ $t('verify_email_text') }}
        </p>
        <q-form @submit.prevent="onSubmit">
          <!-- hide-bottom-space: don't reserve the error-message area — it would
               make the card overflow its height cap and the sticky action bar
               would clip the last checklist row. The error still renders when
               present (content shifts down instead). -->
          <q-input
            v-model="code"
            :label="$t('verification_code')"
            type="text"
            autocomplete="one-time-code"
            hide-bottom-space
            :error="Boolean(codeError)"
            :error-message="codeError"
            @update:model-value="codeError = ''"
          />
          <balm-password-input v-model="password" :label="$t('new_password')" autocomplete="new-password"/>
          <balm-password-input v-model="confirmPassword" :label="$t('confirm_password')" autocomplete="new-password"/>
          <password-requirements-checklist :requirements="requirements"/>
          <div class="actions">
            <balm-btn
              :label="$t('resend_code')"
              theme="grey"
              :disable="!userId"
              @click="onResendClick"
            />
            <balm-btn
              :label="$t('continue')"
              theme="blue"
              type="submit"
              icon="arrow-narrow-right.svg"
              :disable="!canSubmit"
            />
          </div>
        </q-form>
      </div>
      <balm-dialog v-model="captchaDialogOpened" theme="neutral-0" type="fit-content" :blur-backdrop="true">
        <div class="captcha-dialog">
          <h2>{{ $t('verify_youre_human') }}</h2>
          <!-- Private Captcha refuses to render unless its widget lives inside a
               <form> ancestor (it logs "cannot find form element" and paints nothing). -->
          <form v-show="!captchaScriptFailed" @submit.prevent>
            <sign-up-captcha
              v-if="captchaDialogOpened"
              ref="captchaRef"
              :sitekey="captchaSitekey"
              hide-title
              @script-failed="captchaScriptFailed = $event"
            />
          </form>
          <div v-if="captchaScriptFailed" class="captcha-load-error">
            <p>{{ $t('captcha_load_error') }}</p>
            <balm-btn :label="$t('try_again')" theme="blue" @click="captchaRef?.resetAndRemount()"/>
          </div>
        </div>
      </balm-dialog>
    </public-auth-template>
  </loading-error>
</template>
<style lang="scss" scoped>
.verify-email {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);

  h1 {
    @include font-display-4;
    margin: 0;
    text-align: center;
  }

  .subtitle {
    @include font-body;
    margin: 0;
    text-align: center;
  }
}

.verify-email--success {
  align-items: center;
  gap: var(--Spacing-spacing-5);
  padding-top: var(--Spacing-spacing-5);
  // No sticky action bar on this view, so it owns the card's bottom padding.
  padding-bottom: var(--Spacing-spacing-6);
}

.success-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: var(--Functional-wl-completed-surface);
  color: var(--Neutral-UI-neutral-ui-0);
}

.q-form {
  display: flex;
  flex-direction: column;
  // Tight enough that the full form + checklist + action bar fit the card's
  // 720px height cap without scrolling on desktop.
  gap: var(--Spacing-spacing-1);
}

// Pinned to the card's bottom edge while the form scrolls (same sticky bar as
// the sign-up stepper footer).
.actions {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-4);
  position: sticky;
  bottom: 0;
  padding-top: var(--Spacing-spacing-4);
  padding-bottom: var(--Spacing-spacing-4);
  // Solid to mask scrolling content; must match the auth card's ui-0 surface.
  background: var(--Neutral-UI-neutral-ui-0);

  // Both buttons share the row evenly (same pattern as the consent footer).
  :deep(.balm-btn) {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
    padding-left: var(--Spacing-spacing-4);
    padding-right: var(--Spacing-spacing-4);
  }
}


.full-btn {
  width: 100%;

  :deep(.balm-btn) {
    width: 100%;
  }
}

.captcha-load-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-4);
  margin-top: var(--Spacing-spacing-4);
  text-align: center;
}

.captcha-dialog {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
  padding: var(--Spacing-spacing-4);

  h2 {
    @include font-h2;
    margin: 0;
  }
}

</style>
